import { KText } from '@/ui-library/components/text';
import { Avatar, Flex, Grid, Link } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { ViewFile } from '../FileUpload';
import { useDateFormat } from '@/hooks/useDateFormat';
import { ensureProtocol } from '@/utils/ensureProtocol';

const MILLISECOND_TIMESTAMP_MIN_LENGTH = 13;

interface ApplicationValueProps {
  field: Record<string, any>;
  ats_attachments?: any[];
}

// Utility function to determine if timestamp is in seconds or milliseconds
const formatTimestamp = (timestamp: unknown, format: string) => {
  if (!timestamp || isNaN(Number(timestamp))) return '-';
  if (timestamp.toString().length >= MILLISECOND_TIMESTAMP_MIN_LENGTH) {
    return dayjs(Number(timestamp)).format(format);
  } else {
    return dayjs.unix(Number(timestamp)).format(format);
  }
};

export const ApplicationValue = ({
  field,
  ats_attachments = [],
}: ApplicationValueProps) => {
  const { id, type, value, submitted_value, kind, values } = field;
  const { dateFormat } = useDateFormat();

  if (kind === 'url' || type === 'url') {
    const actualValue = value || submitted_value;
    // Answers are stored as typed and the URL validators make the scheme
    // optional, so a bare `linkedin.com/in/x` would resolve in-app. ENG-3662.
    const href = ensureProtocol(actualValue);
    return actualValue ? (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        color="ats-primary"
        cursor="pointer"
        width="fit-content"
      >
        <KText size="sm-regular" whiteSpace="pre-wrap" wordBreak="break-word">
          {actualValue}
        </KText>
      </Link>
    ) : (
      <KText size="sm-regular">-</KText>
    );
  }

  if (kind === 'rel_file' || type === 'rel_file') {
    let attachment;
    if (typeof value === 'object' && value?.url) {
      // If value is an object with a url property, use it directly
      attachment = value;
    } else {
      // Otherwise, search in ats_attachments
      const foundAttachment = ats_attachments?.find(
        (item) => item?.id === submitted_value || item?.id === value
      );
      attachment = foundAttachment?.attachment || {};
    }
    return attachment?.url ? (
      <ViewFile file={attachment} />
    ) : (
      <KText size="sm-regular">-</KText>
    );
  }

  if (kind === 'date') {
    const actualValue = value || submitted_value;

    return (
      <KText size="sm-regular">
        {formatTimestamp(actualValue, dateFormat.label) || '-'}
      </KText>
    );
  }

  if (kind === 'enum_boolean') {
    // note: commenting custom label for this, if needed uncomment in future
    // const { options } = field;
    // const actualValue = options?.labels?.[submitted_value];
    // return <Text size="sm-regular">{actualValue || '-'}</Text>;

    const valueMap = {
      Y: 'Yes',
      N: 'No',
    };

    return (
      <KText size="sm-regular">{valueMap?.[submitted_value] || '-'}</KText>
    );
  }

  if (kind === 'rel_tag') {
    let actualValueArr;
    if (Array.isArray(submitted_value)) {
      actualValueArr = submitted_value?.map(
        (itemSub) => values?.find((item) => item?.id === itemSub)?.value
      );
    }
    const actualValue = values?.find((item) => item?.id === submitted_value);
    return (
      <KText size="sm-regular">
        {actualValue?.value ||
          (actualValueArr?.length && actualValueArr?.join(',')) ||
          '-'}
      </KText>
    );
  }

  // Render Education/ Experience
  if (type === 'complex' && (id === 'education' || id === 'experience')) {
    return (
      <Grid gap="10px" mt="6px" templateColumns="repeat(3, 1fr)">
        {value?.map?.((item, idx) => {
          const {
            company,
            academic_institution,
            academic_degree,
            title,
            start_date,
            end_date,
          } = item;
          const avatarSrc = company?.logo;
          const headerName = company?.name || academic_institution?.name || '';
          const subHeaderName = title || academic_degree?.name;
          const dateText = `${dayjs(start_date).format('MM/YYYY')} -${' '}
					${end_date ? dayjs(end_date).format('MM/YYYY') : 'Present'}`;

          return (
            <Flex gap="12px" key={idx}>
              <Avatar
                w="32px"
                h="32px"
                borderRadius="6px"
                src={avatarSrc}
                name={headerName}
                className="avatar-md"
              />
              <Flex flexDir="column" gap="4px">
                <KText size="sm-semi-bold">{headerName}</KText>
                <KText size="sm-regular">{subHeaderName}</KText>
                <KText size="sm-regular">{dateText}</KText>
              </Flex>
            </Flex>
          );
        })}
      </Grid>
    );
  }

  return (
    <KText size="sm-regular" whiteSpace="pre-wrap" wordBreak="break-word">
      {value || submitted_value || '-'}
    </KText>
  );
};
